import { SectionTab } from "polotno/side-panel";
import { MusicNote01Icon } from "../hugeIcons/hugeIcons";
import { useGlobalContext } from "@src/context/globalContext";
import { Button, Input, Upload, message, Card, Flex, Slider, Tabs } from "antd";
import { PlayCircleOutlined, PauseCircleOutlined, UploadOutlined, PlusOutlined, SoundOutlined } from "@ant-design/icons";
import React, { useState, useRef, useEffect } from "react";
import { sfxList as defaultSfxList } from "@src/shared/utils/sfsx/sfxlist";
import { uploadFile } from '@src/shared/utils/VideoGeneration/uploadAsset';
import { useGetBGMusic } from '@src/api/useGetBgMusic';
import { v4 as uuidv4 } from 'uuid';
import { aiTextToSoundEffect, getAudioDurationInSecondsCustom } from '@src/shared/utils/VideoGeneration/audioGeneration';

import './customSfxPanel.less';

interface SFX {
    url: string;
    duration: number;
    start: number;
    end: number;
    align: 'left' | 'right';
    volume: number;
    name: string;
    section: number;
    id: string;
}

const SfxCard = ({ sfx, onAdd }: { sfx: any; onAdd: (sfx: any) => void }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [volume, setVolume] = useState(0.8);

    const handlePlayPause = () => {
        if (!audioRef.current) {
            audioRef.current = new Audio(sfx.url);
            audioRef.current.addEventListener('ended', () => setIsPlaying(false));
        }

        if (isPlaying) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
        } else {
            audioRef.current.volume = volume;
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    return (
        <div className="sfx-card">
            <div className="play-button" onClick={handlePlayPause}>
                {isPlaying ? (
                    <PauseCircleOutlined className="play-icon" />
                ) : (
                    <PlayCircleOutlined className="play-icon" />
                )}
            </div>
            <div className="sfx-content">
                <div className="sfx-details">
                    <div className="name-duration">
                        <span className="name">{sfx.name}</span>
                        <span className="duration">{sfx.duration.toFixed(2)}s</span>
                    </div>
                    <div className="volume-bar">
                        <Slider
                            min={0}
                            max={1}
                            step={0.1}
                            value={volume}
                            onChange={setVolume}
                            tooltip={{ formatter: (value) => value ? `${Math.round(value * 100)}%` : '0%' }}
                        />
                    </div>
                </div>
                <Button 
                    type="text"
                    className="add-button"
                    icon={<PlusOutlined />}
                    onClick={() => onAdd({ ...sfx, volume })}
                />
            </div>
        </div>
    );
};

const SfxPanel = () => {
    const { 
        ScriptDetails: [scriptDetails, setScriptDetails],
        SelectedSectionIndex: [selectedSectionIndex],
        CurrentFrame: [currentFrame]
    } = useGlobalContext();

    const [customSfxName, setCustomSfxName] = useState('');
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [currentTimeInSeconds, setCurrentTimeInSeconds] = useState(0);
    const [allSfx, setAllSfx] = useState(defaultSfxList);
    const [customSoundText, setCustomSoundText] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);

    // Fetch SFX using the same API as background music but with 'sfx' type
    const {
        data: sfxData,
        isError,
        error,
        refetch: refetchSfx,
    } = useGetBGMusic(1, 'audio', 'sfx');

    // Update allSfx when sfxData changes
    useEffect(() => {
        const fetchDurations = async () => {
            if (sfxData?.items && sfxData.items.length > 0) {
                try {
                    // Filter items with sub_type 'sfx' and fetch durations
                    const sfxItems = sfxData.items.filter((item: any) => item.sub_type === 'sfx');
                   
                    const uploadedSfxPromises = sfxItems.map(async (item: any) => {
                        let duration = 3; // Default duration
                        try {
                            duration = await getAudioDurationInSecondsCustom(item.audio_url);
                        } catch (error) {
                            console.warn(`Could not get duration for ${item.name}, using default 3s`);
                        }
                        return {
                            name: item.asset_name,
                            url: item.audio_url,
                            duration: duration
                        };
                    });

                    const uploadedSfx = await Promise.all(uploadedSfxPromises);
                    setAllSfx([...defaultSfxList, ...uploadedSfx.reverse()]);
                } catch (error) {
                    console.error('Error processing uploaded SFX:', error);
                }
            }
        };

        fetchDurations();
    }, [sfxData]);

    // Initial fetch on mount
    useEffect(() => {
        refetchSfx();
    }, []);

    // Calculate current time in seconds from frame
    useEffect(() => {
        const timeInSeconds = (currentFrame / 30);
        setCurrentTimeInSeconds(timeInSeconds);
      
    }, [currentFrame]);

    const handleSfxAdd = (sfx: any) => {
        if (selectedSectionIndex === null) {
            message.warning('Please select a section first to add SFX');
            return;
        }

        setScriptDetails((prevScript: any) => {
            const updatedScript = { ...prevScript };
            // Calculate end time with 4 second maximum span
            const maxDuration = 4; // Maximum duration in seconds
            const actualDuration = Math.min(sfx.duration, maxDuration);
            const endTime = currentTimeInSeconds + actualDuration;

            const newSfx: SFX = {
                url: sfx.url,
                duration: sfx.duration, // Keep original duration for reference
                start: currentTimeInSeconds,
                end: endTime,
                align: 'left',
                volume: sfx.volume || 0.8,
                name: sfx.name,
                section: selectedSectionIndex,
                id: uuidv4(),
            };

            // Initialize sfxs array if it doesn't exist
            if (!updatedScript.sfxs) {
                updatedScript.sfxs = [];
            }

            updatedScript.sfxs = [...updatedScript.sfxs, newSfx];
            return updatedScript;
        });

        message.success(
            `Added ${sfx.name} at ${currentTimeInSeconds.toFixed(2)}s` + 
            (sfx.duration > 4 ? ' (trimmed to 4s)' : '')
        );
    };

    const handleCustomUpload = async () => {
        if (!uploadedFile) {
            message.warning('Please select an audio file');
            return;
        }

        setIsUploading(true);
        try {
            // Extract the file name without extension
            const fileName = uploadedFile.name;
            let baseFileName = customSfxName || fileName.split('.').slice(0, -1).join('.') || fileName;
            
            // Truncate name if longer than 50 characters
            if (baseFileName.length > 50) {
                baseFileName = baseFileName.substring(0, 47) + '...';
            }
            
            // Generate unique name by adding UUID
            const uniqueSfxName = `${baseFileName}-${uuidv4().slice(0, 8)}`;
            
            // Upload the file with unique name
            await uploadFile(uploadedFile, uniqueSfxName, 'sfx');

            // Refetch the SFX list to include the new upload
            await refetchSfx();
            
            message.success('Sound effect uploaded successfully');
            setCustomSfxName('');
            setUploadedFile(null);
        } catch (error) {
            console.error('Error uploading sound effect:', error);
            message.error('Failed to upload sound effect');
        }
        setIsUploading(false);
    };

    const handleGenerateCustomSound = async () => {
        if (!customSoundText.trim()) {
            message.warning('Please describe the sound you want to generate');
            return;
        }

        setIsGenerating(true);
        try {
            // Get the generated sound URL
            const generatedSoundUrl = await aiTextToSoundEffect(customSoundText, 1);
            
            // Download the file from the generated URL
            const response = await fetch(generatedSoundUrl);
            const blob = await response.blob();
            
            // Create a File object from the blob
            const fileName = `${customSoundText.slice(0, 30)}-${uuidv4().slice(0, 8)}.mp3`;
            const audioFile = new File([blob], fileName, { type: 'audio/mpeg' });
            
            // Upload the file to our storage
            const uploadedUrl = await uploadFile(audioFile, fileName, 'sfx');
            
            // Get duration of the uploaded sound, default to 3 seconds if fails
            let duration = 3;
            try {
                duration = await getAudioDurationInSecondsCustom(uploadedUrl?.audio_url);
            } catch (error) {
                console.warn('Could not get duration for generated sound, using default 3s');
            }
            
            // Add to allSfx state
            const newSfx = {
                name: customSoundText,
                url: uploadedUrl?.audio_url,
                duration: duration
            };
            
            setAllSfx(prev => [...prev, newSfx]);
            
            message.success('Sound effect generated and uploaded successfully');
            setCustomSoundText('');
        } catch (error) {
            console.error('Error generating/uploading sound effect:', error);
            message.error('Failed to generate sound effect');
        } finally {
            setIsGenerating(false);
        }
    };

    // Audio file validation
    const validateAudioFile = (file: File) => {
        const acceptedTypes = [
            'audio/mpeg',        // .mp3
            'audio/wav',         // .wav
            'audio/ogg',         // .ogg
            'audio/x-m4a',       // .m4a
            'audio/aac',         // .aac
            'audio/mp4'          // .mp4 audio
        ];

        if (!acceptedTypes.includes(file.type)) {
            message.error('Please upload an audio file (MP3, WAV, OGG, M4A, AAC)');
            return false;
        }

        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            message.error('File size must be less than 10MB');
            return false;
        }

        return true;
    };

    return (
        <div className="sfx-panel-container">
            <div className="header">
                <h1>Sound Effects</h1>
                <p className="instruction">
                    Select a section in timeline or move playhead to where you want the sound,
                    then click the + button to add it at that position. Adjust volume before adding.
                </p>
                <div className="current-time">
                    Current Position: {currentTimeInSeconds.toFixed(2)}s
                </div>
            </div>

            <div className="sfx-library">
                <h2>Sound Effects Library</h2>
                <div className="sfx-grid">
                    {allSfx.map((sfx, index) => (
                        <SfxCard 
                            key={`${sfx.name}-${index}`} 
                            sfx={sfx} 
                            onAdd={handleSfxAdd}
                        />
                    ))}
                </div>
            </div>

            <div className="custom-sfx-section">
                <Tabs
                    defaultActiveKey="generate"
                    items={[
                        {
                            key: 'generate',
                            label: 'Generate Sound',
                            children: (
                                <div className="generate-sfx">
                                    <h3>Generate Sound Effect</h3>
                                    <Flex vertical gap={10}>
                                        <Input.TextArea
                                            placeholder="Describe the sound effect you want (e.g., 'gentle rain falling on a tin roof')"
                                            value={customSoundText}
                                            onChange={(e) => setCustomSoundText(e.target.value)}
                                            rows={2}
                                        />
                                        <Button
                                            type="primary"
                                            icon={<SoundOutlined />}
                                            onClick={handleGenerateCustomSound}
                                            loading={isGenerating}
                                            disabled={!customSoundText.trim()}
                                        >
                                            Generate Sound Effect
                                        </Button>
                                    </Flex>
                                </div>
                            ),
                        },
                        {
                            key: 'upload',
                            label: 'Upload Sound',
                            children: (
                                <div className="upload-sfx">
                                    <h3>Upload Custom Sound</h3>
                                    <Flex vertical gap={10}>
                                        <Input
                                            placeholder="Sound Effect Name (optional)"
                                            value={customSfxName}
                                            onChange={(e) => setCustomSfxName(e.target.value)}
                                        />
                                        <Upload
                                            accept=".mp3,.wav,.ogg,.m4a,.aac"
                                            beforeUpload={(file) => {
                                                if (validateAudioFile(file)) {
                                                    setUploadedFile(file);
                                                }
                                                return false;
                                            }}
                                            maxCount={1}
                                            showUploadList={{
                                                showPreviewIcon: false,
                                                showRemoveIcon: true,
                                            }}
                                            onRemove={() => setUploadedFile(null)}
                                        >
                                            <Button icon={<UploadOutlined />}>
                                                Select Audio File
                                            </Button>
                                        </Upload>
                                        <Button
                                            type="primary"
                                            onClick={handleCustomUpload}
                                            loading={isUploading}
                                            disabled={!uploadedFile}
                                        >
                                            Upload Sound Effect
                                        </Button>
                                    </Flex>
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        </div>
    );
};

export default SfxPanel;

export const CustomSfxPanel = {
    name: 'sfx-panel',
    Tab: (props: any) => (
        <SectionTab name="SFX" {...props}>
            <MusicNote01Icon />
        </SectionTab>
    ),
    Panel: SfxPanel,
};